import React from "react";
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

// ICONS
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { getBenefitName, getBenefitTarget } from "../../../../extras";
import { formatFinance } from "../../../../utils/formatters";

// CHART COLORS
const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#9370DB",
  "#3CB371",
  "#DC143C",
  "#4682B4",
];

export const BenefitsStats = ({ data, loading }) => {
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Se não houver dados suficientes
  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <LocalOfferIcon sx={{ mr: 1, color: "info.main" }} />
          <Typography variant="h6">Benefícios e Promoções</Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
            flexDirection: "column",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Não há dados de benefícios disponíveis para o período selecionado.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Preparar dados para o gráfico
  const chartData = data.slice(0, 5).map((item, index) => {
    const fullName = `${getBenefitName(item.name)} - ${getBenefitTarget(
      item.target
    )}`;
    return {
      name:
        fullName.length > 10 ? fullName.substring(0, 10) + "..." : item.name,
      value: item.totalValue,
      orderCount: item.orderCount,
      color: COLORS[index % COLORS.length],
      fullName: fullName,
    };
  });

  // Calcular o valor total de todos os benefícios
  const totalBenefitValue = data.reduce(
    (acc, item) => acc + item.totalValue,
    0
  );

  // Função personalizada para o tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="custom-tooltip"
          style={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "5px",
          }}
        >
          <p className="tooltip-label" style={{ margin: 0 }}>
            <strong>{payload[0].payload.fullName}</strong>
          </p>
          <p className="tooltip-value" style={{ margin: 0 }}>
            Valor: {formatFinance(payload[0].value)}
          </p>
          <p className="tooltip-count" style={{ margin: 0 }}>
            Pedidos: {payload[0].payload.orderCount}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <LocalOfferIcon sx={{ mr: 1, color: "info.main" }} />
        <Typography variant="h6">Benefícios e Promoções</Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Top 5 Benefícios por Valor Total
          </Typography>
          <Box sx={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 5, right: 5, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Valor">
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Resumo de Benefícios
          </Typography>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ height: 240, overflowY: "auto" }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Benefício</TableCell>
                  <TableCell align="right">Pedidos</TableCell>
                  <TableCell align="right">Valor Médio</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell
                      sx={{
                        maxWidth: 120,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {getBenefitName(item.name)} -{" "}
                      {getBenefitTarget(item.target)}
                    </TableCell>
                    <TableCell align="right">{item.orderCount}</TableCell>
                    <TableCell align="right">
                      {formatFinance(item.averageValue)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Total de Benefícios Concedidos:</strong>
              {formatFinance(totalBenefitValue)}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};
